import { forwardRef, useEffect, useRef, useState } from "react";
import { bool, func, number, oneOfType, shape, string } from "prop-types";

import { URL_API_WATCH } from "../../constants";
import { api, getApiErrorMessage } from "../../helpers";

import ButtonIcon from "../../styles/components/ButtonIcon";

const getIcon = isWatched => (isWatched ? "bookmarkSolid" : "bookmark");
const getLabel = isWatched => (isWatched ? "Watching" : "Watch");

export const WatchButtonControl = forwardRef(
  ({ iconColor = "", inProgress, isWatched, ...rest }, ref) => {
    const [icon, setIcon] = useState(getIcon(isWatched));
    const [label, setLabel] = useState(getLabel(isWatched));

    useEffect(() => {
      setIcon(getIcon(isWatched));
      setLabel(getLabel(isWatched));
    }, [isWatched]);

    return (
      <ButtonIcon
        {...rest}
        disabled={inProgress}
        icon={icon}
        iconColor={iconColor}
        ref={ref}
        type="button"
      >
        {label}
        {inProgress ? "…" : ""}
      </ButtonIcon>
    );
  }
);

const WatchButton = ({
  addWatchedListing,
  analyticsLabel,
  isWatched,
  listingId,
  isWatchPatch,
  removeWatchedListing,
  user,
  trackEvent,
  ...rest
}) => {
  const [inProgress, setInProgress] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleError = error => {
    if (isMounted.current) {
      setInProgress(false);
    }

    const action = isWatched ? "unwatch" : "watch";
    const description = isMounted ? `Unable to ${action} listing` : "";
    const message = getApiErrorMessage(error);
    const detail = !!message ? ` (${message})` : "";
    const userFriendlyError = new Error(description + detail);

    if (!!error && !!error.stack) {
      userFriendlyError.stack += `\nCaused by: ${error.stack}`;
    }

    throw userFriendlyError;
  };

  const handleWatch = () => {
    setInProgress(true);

    const data = {
      id: 0,
      listingId: listingId,
      email: !!user && user.email,
      userId: !!user && user.id,
    };

    api
      .post(URL_API_WATCH, data)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (trackEvent) {
            trackEvent({
              category: "click",
              action: "Watch Listing",
              label: analyticsLabel,
            });
          }

          addWatchedListing(listingId);

          if (isMounted.current) {
            setInProgress(false);
          }
        } else {
          handleError(response);
        }
      })
      .catch(handleError);
  };

  const handleUnwatch = () => {
    setInProgress(true);

    api
      .delete(`${URL_API_WATCH}/${listingId}`)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (trackEvent) {
            trackEvent({
              category: "click",
              action: "Unwatch Listing",
              label: analyticsLabel,
            });
          }

          removeWatchedListing(listingId, isWatchPatch);

          if (isMounted.current) {
            setInProgress(false);
          }
        } else {
          handleError(response);
        }
      })
      .catch(handleError);
  };

  return (
    <WatchButtonControl
      {...rest}
      inProgress={inProgress}
      isWatched={isWatched}
      onClick={() => (isWatched ? handleUnwatch() : handleWatch())}
    />
  );
};

WatchButton.propTypes = {
  addWatchedListing: func.isRequired,
  analyticsLabel: string,
  isWatched: bool,
  listingId: oneOfType([number, string]),
  isWatchPatch: bool,
  removeWatchedListing: func.isRequired,
  user: shape({
    email: string,
  }),
  trackEvent: func,
};

export default WatchButton;
